import React, { useState } from 'react'
import styles from 'components/people/people.module.scss'
import UserIcon from 'components/users/UserIcon'
import ThinQuest from 'components/quests/ThinQuest'
import useOpenRight from 'refactor/hooks/useOpenRight'
import usePanelContext from 'refactor/hooks/usePanelContext'
import useGetUser from 'refactor/hooks/api/useGetUser'
import cn from 'classnames'

export function Participants({ participants, quest, onClick = null, contextStyles = '' }) {
    return (
        <div
            className={cn(styles.participantsComp, contextStyles)}
            onClick={() => onClick && onClick()}
        >
            <div className={styles.participantsHeader}>
                <div className={styles.participantsLabel}>Participants:</div>

                {quest && (
                    <div className={styles.timeWrapper}>
                        <Time time={quest.updated_at} />
                    </div>
                )}
            </div>

            <div className={styles.participantsWrapper}>
                {participants.length === 1 && (
                    <div className={styles.firstParticipant}>
                        <UserIcon
                            user={participants[0]}
                            size={'medium'}
                            contextStyles={styles.userIcon}
                        />
                        <span className={styles.participantName}>
                            {participants[0].name || 'Unknown Participant'}
                        </span>
                    </div>
                )}

                {participants.length > 1 && (
                    <div className={styles.multiParticipants}>
                        {participants.map((participant, index) => (
                            <div key={participant.id || index} className={styles.multiParticipant}>
                                <UserIcon
                                    user={participant}
                                    contextStyles={styles.multiUserIcon}
                                    size={'medium'}
                                />
                                <div className={styles.multiParticipantName}>
                                    {participant.name || 'Unknown'}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}

export function nonUserParticipants(quest) {
    const sortedAnswers = quest?.sorted_answers?.filter(Boolean) || []
    return quest?.participants
        ?.filter(participant => participant && participant.id !== gon?.currentUser?.id)
        ?.sort((a, b) => {
            const aLatestAnswer = sortedAnswers
                .filter(answer => answer && answer.user_id === a.id)
                .sort(
                    (x, y) => new Date(y.created_at).getTime() - new Date(x.created_at).getTime(),
                )[0]
            const bLatestAnswer = sortedAnswers
                .filter(answer => answer && answer.user_id === b.id)
                .sort(
                    (x, y) => new Date(y.created_at).getTime() - new Date(x.created_at).getTime(),
                )[0]
            return (
                new Date(bLatestAnswer?.created_at || 0).getTime() -
                new Date(aLatestAnswer?.created_at || 0).getTime()
            )
        })
}

// Component to display participants when we only have user IDs (no quest data)
// Used when viewing conversations with a user where no conversations exist yet
export function ParticipantsFromIds({ participantIds, contextStyles }) {
    // Fetch user data for each participant ID
    // We support up to 2 participants for the common case of viewing conversations between two users
    const { user: user1 } = useGetUser(participantIds[0])
    const { user: user2 } = useGetUser(participantIds[1])

    const participants = [user1, user2].filter(Boolean)

    if (participants.length === 0) {
        return null
    }

    return (
        <div className={cn(styles.participantsComp, contextStyles)}>
            <div className={styles.participantsHeader}>
                <div className={styles.participantsLabel}>Conversation with:</div>
            </div>

            <div className={styles.participantsWrapper}>
                {participants.length === 1 && (
                    <div className={styles.firstParticipant}>
                        <UserIcon
                            user={participants[0]}
                            size={'medium'}
                            contextStyles={styles.userIcon}
                        />
                        <span className={styles.participantName}>
                            {participants[0].name || participants[0].username || 'Unknown'}
                        </span>
                    </div>
                )}

                {participants.length > 1 && (
                    <div className={styles.multiParticipants}>
                        {participants.map((participant, index) => (
                            <div key={participant.id || index} className={styles.multiParticipant}>
                                <UserIcon
                                    user={participant}
                                    contextStyles={styles.multiUserIcon}
                                    size={'medium'}
                                />
                                <div className={styles.multiParticipantName}>
                                    {participant.name || participant.username || 'Unknown'}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}

const People = ({ quest }) => {
    const { panel } = usePanelContext()
    const { openRight } = useOpenRight(panel?.panelId)
    const [hover, setHover] = useState(false)

    // Early return if quest is null/undefined
    if (!quest) {
        return (
            <div className={styles.peopleComp}>
                <div className={styles.noParticipants}>No quest data available</div>
            </div>
        )
    }

    const onClick = () => {
        if (quest && quest.participants_key) {
            openRight({ participants: quest.participants_key }, { meta: { questId: quest.id } })
        }
    }

    const participants = nonUserParticipants(quest)

    const firstParticipant = participants && participants[0]

    const displayAnswerIds =
        firstParticipant && quest?.sorted_answers
            ? quest.sorted_answers
                  .filter(Boolean)
                  .filter(answer => answer && answer.user_id === firstParticipant?.id)
                  .sort(
                      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime(),
                  )
                  .slice(0, 1)
                  .map(answer => answer.id)
            : []

    // Always show last message for ease of identifying thread.
    const lastAnswer =
        quest?.sorted_answers && quest.sorted_answers.length > 0
            ? quest.sorted_answers[quest.sorted_answers.length - 1]
            : null
    if (lastAnswer && !displayAnswerIds.includes(lastAnswer.id)) {
        displayAnswerIds.push(lastAnswer.id)
    }

    return (
        <div
            className={cn(styles.peopleComp, hover && styles.hover)}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onClick={onClick}
        >
            {participants && participants.length > 0 ? (
                <>
                    <Participants participants={participants} quest={quest} />
                    <div className={styles.lastThreadLabel}>Last thread:</div>

                    <div
                        className={styles.questRow}
                        onMouseEnter={() => setHover(false)}
                        onMouseLeave={() => setHover(true)}
                    >
                        <ThinQuest
                            quest={quest}
                            displayAnswerIds={displayAnswerIds}
                            contextStyles={cn(styles.thinQuest, hover && styles.thinQuestBorder)}
                        />
                    </div>

                    {quest.participants_key_count - 1 > 0 && (
                        <div className={styles.participantsCountWrapper}>
                            <span className={styles.participantsCount}>
                                {quest.participants_key_count - 1} more{' '}
                                {quest.participants_key_count - 1 === 1 ? 'thread' : 'threads'}.
                            </span>
                        </div>
                    )}
                </>
            ) : (
                <div className={styles.noParticipants}>
                    No participants found for quest {quest.id}
                </div>
            )}
        </div>
    )
}

export default People

function Time(props) {
    const { time } = props
    const today = new Date()
    const givenDate = new Date(time)

    const isToday = givenDate.toDateString() === today.toDateString()

    if (isToday) {
        return (
            <div className={styles.time}>
                {givenDate.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </div>
        )
    } else {
        return <div className={styles.time}>{givenDate.toLocaleDateString()}</div>
    }
}
